import React, { useState, useEffect } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import { styles, colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const Header = ({ title }) => (
    <div style={{
        background: `linear-gradient(135deg, ${styles.primary} 0%, ${styles.primaryDark} 100%)`,
        color: 'white',
        padding: '15px 25px',
        paddingRight: '60px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }}>
        <h1 style={{ margin: 0, fontSize: '22px' }}>{title}</h1>
        <span style={{ fontSize: '14px' }}>{new Date().toLocaleDateString('tr-TR')}</span>
    </div>
);

const getSkorColor = (skor) => {
    if (skor >= 8) return { bg: '#d4edda', color: '#155724', border: '#28a745' };
    if (skor >= 5) return { bg: '#fff3cd', color: '#856404', border: '#ffc107' };
    return { bg: '#f8d7da', color: '#721c24', border: '#dc3545' };
};

const getTrendIcon = (trend) => {
    if (trend === 'yukari') return '↑';
    if (trend === 'dusuk') return '↓';
    return '→';
};

const getTrendColor = (trend, reverse) => {
    // reverse: for fire/price, "yukari" is bad
    if (reverse) {
        if (trend === 'yukari') return '#dc3545';
        if (trend === 'dusuk') return '#28a745';
        return '#6c757d';
    }
    if (trend === 'yukari') return '#28a745';
    if (trend === 'dusuk') return '#dc3545';
    return '#6c757d';
};

const TedarikçiKarnesiPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [months, setMonths] = useState(6);
    const token = localStorage.getItem('access_token');

    useEffect(() => {
        fetchScorecard();
    }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchScorecard = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/reports/supplier-scorecard?months=${months}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const result = await res.json();
                setData(result);
                setError(null);
                if (result.suppliers.length > 0 && !selectedSupplier) {
                    setSelectedSupplier(result.suppliers[0]);
                } else if (selectedSupplier) {
                    const updated = result.suppliers.find(s => s.tedarikci_id === selectedSupplier.tedarikci_id);
                    setSelectedSupplier(updated || result.suppliers[0] || null);
                }
            } else {
                // 500'ü yutma: eskiden data=null kalıp "Henüz veri yok" görünüyordu
                const err = await res.json().catch(() => ({}));
                setError(err.detail || `Sunucu hatası (${res.status})`);
            }
        } catch (err) {
            setError('Bağlantı/sunucu hatası');
            logError('Scorecard fetch error:', err);
        }
        setLoading(false);
    };

    if (loading && !data) {
        return (
            <div>
                <Header title="📊 Tedarikçi Karnesi" />
                <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
            </div>
        );
    }

    if (error) {
        // Sunucu hatasını "veri yok" gibi gösterme — yanıltıcı (Ürün Satış Raporu dersi)
        return (
            <div>
                <Header title="📊 Tedarikçi Karnesi" />
                <div style={{ padding: '24px' }}>
                    <ErrorBanner error={error} onRetry={fetchScorecard} />
                </div>
            </div>
        );
    }

    if (!data || data.suppliers.length === 0) {
        return (
            <div>
                <Header title="📊 Tedarikçi Karnesi" />
                <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>Henüz tedarikçi değerlendirme verisi yok.</div>
                    <div style={{ fontSize: '14px', marginTop: '10px' }}>
                        GRN kalite kontrol ve karkas parçalama kayıtları oluştukça veriler burada görünecek.
                    </div>
                </div>
            </div>
        );
    }

    const sup = selectedSupplier;

    return (
        <div>
            <Header title="📊 Tedarikçi Karnesi" />

            {/* Ay Filtresi */}
            <div style={{ padding: '15px 25px', background: colors.backgroundAlt, borderBottom: `1px solid ${colors.border}`, display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Dönem:</span>
                {[3, 6, 12].map(m => (
                    <button
                        key={m}
                        onClick={() => setMonths(m)}
                        style={{
                            padding: '6px 16px',
                            borderRadius: radius.xl,
                            border: months === m ? `2px solid ${styles.primary}` : `1px solid ${colors.border}`,
                            background: months === m ? styles.primary : 'white',
                            color: months === m ? 'white' : '#333',
                            cursor: 'pointer',
                            fontWeight: months === m ? 'bold' : 'normal',
                            fontSize: '13px'
                        }}
                    >
                        Son {m} Ay
                    </button>
                ))}
                <span style={{ marginLeft: 'auto', color: '#888', fontSize: '13px' }}>
                    {data.total_count} tedarikçi değerlendirildi
                </span>
            </div>

            <div style={{ padding: '20px', display: 'flex', gap: '20px' }}>
                {/* Sol Panel - Tedarikçi Listesi */}
                <div style={{ width: '320px', flexShrink: 0 }}>
                    <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                        <div style={{ padding: '15px', borderBottom: `1px solid ${colors.border}` }}>
                            <h3 style={{ margin: 0, fontSize: '16px' }}>🏭 Tedarikçiler</h3>
                        </div>
                        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {data.suppliers.map(s => {
                                const sc = getSkorColor(s.genel_skor);
                                const isSelected = sup?.tedarikci_id === s.tedarikci_id;
                                return (
                                    <div
                                        key={s.tedarikci_id}
                                        onClick={() => setSelectedSupplier(s)}
                                        style={{
                                            padding: '14px 15px',
                                            borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer',
                                            background: isSelected ? '#e3f2fd' : 'white',
                                            borderLeft: `4px solid ${sc.border}`,
                                            transition: 'background 0.15s'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{s.tedarikci_ad}</div>
                                            <div style={{
                                                background: sc.bg,
                                                color: sc.color,
                                                padding: '3px 10px',
                                                borderRadius: radius.lg,
                                                fontWeight: 'bold',
                                                fontSize: '13px'
                                            }}>
                                                {s.genel_skor}/10
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '12px', color: '#888' }}>
                                            {s.kalite.kontrol_sayisi > 0 && (
                                                <span>Kalite: {s.kalite.ortalama_puan}/5</span>
                                            )}
                                            {s.randiman.parcalama_sayisi > 0 && (
                                                <span>Randıman: %{s.randiman.ortalama_pct}</span>
                                            )}
                                        </div>
                                        {s.uyarilar.length > 0 && (
                                            <div style={{ marginTop: '4px' }}>
                                                {s.uyarilar.slice(0, 2).map((u, i) => (
                                                    <div key={i} style={{
                                                        fontSize: '11px',
                                                        color: u.tip === 'kritik' ? '#dc3545' : '#ffc107',
                                                        fontWeight: u.tip === 'kritik' ? 'bold' : 'normal'
                                                    }}>
                                                        {u.tip === 'kritik' ? '🔴' : '⚠️'} {u.mesaj}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Sağ Panel - Detay */}
                <div style={{ flex: 1 }}>
                    {sup ? (
                        <div>
                            {/* Başlık + Genel Skor */}
                            <div style={{
                                background: colors.surface,
                                borderRadius: radius.md,
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h2 style={{ margin: 0, fontSize: '20px' }}>🏭 {sup.tedarikci_ad}</h2>
                                        <div style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>
                                            Son {months} aylık değerlendirme
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            background: getSkorColor(sup.genel_skor).bg,
                                            border: `3px solid ${getSkorColor(sup.genel_skor).border}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column'
                                        }}>
                                            <div style={{
                                                fontSize: '24px',
                                                fontWeight: 'bold',
                                                color: getSkorColor(sup.genel_skor).color
                                            }}>
                                                {sup.genel_skor}
                                            </div>
                                            <div style={{ fontSize: '10px', color: '#888' }}>/10</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 4 KPI Kartları */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                {/* Kalite Puanı */}
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '18px',
                                    borderLeft: '4px solid #17a2b8'
                                }}>
                                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Kalite Puanı</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontSize: '28px', fontWeight: 'bold' }}>
                                            {sup.kalite.ortalama_puan ?? '—'}
                                        </span>
                                        <span style={{ color: '#888', fontSize: '14px' }}>/5</span>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTrendColor(sup.kalite.trend, false),
                                            marginLeft: 'auto'
                                        }}>
                                            {getTrendIcon(sup.kalite.trend)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                        {sup.kalite.kontrol_sayisi} kontrol
                                        {sup.kalite.renk_ort && ` • R:${sup.kalite.renk_ort} K:${sup.kalite.koku_ort} D:${sup.kalite.doku_ort}`}
                                    </div>
                                </div>

                                {/* Randıman */}
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '18px',
                                    borderLeft: '4px solid #28a745'
                                }}>
                                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Randıman</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontSize: '28px', fontWeight: 'bold' }}>
                                            {sup.randiman.ortalama_pct != null ? `%${sup.randiman.ortalama_pct}` : '—'}
                                        </span>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTrendColor(sup.randiman.trend, false),
                                            marginLeft: 'auto'
                                        }}>
                                            {getTrendIcon(sup.randiman.trend)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                        {sup.randiman.parcalama_sayisi} parçalama
                                    </div>
                                </div>

                                {/* Fire Oranı */}
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '18px',
                                    borderLeft: '4px solid #ffc107'
                                }}>
                                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Fire Oranı</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontSize: '28px', fontWeight: 'bold' }}>
                                            {sup.randiman.fire_orani_ort != null ? `%${sup.randiman.fire_orani_ort}` : '—'}
                                        </span>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTrendColor(sup.randiman.trend, true),
                                            marginLeft: 'auto'
                                        }}>
                                            {getTrendIcon(sup.randiman.trend)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                        Düşük = iyi
                                    </div>
                                </div>

                                {/* Fiyat */}
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '18px',
                                    borderLeft: '4px solid #6f42c1'
                                }}>
                                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Ort. Fiyat</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontSize: '28px', fontWeight: 'bold' }}>
                                            {sup.fiyat.ortalama_birim_fiyat != null
                                                ? `₺${sup.fiyat.ortalama_birim_fiyat.toLocaleString('tr-TR')}`
                                                : '—'}
                                        </span>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: getTrendColor(sup.fiyat.trend, true),
                                            marginLeft: 'auto'
                                        }}>
                                            {getTrendIcon(sup.fiyat.trend)}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                        {sup.fiyat.son_fiyat != null ? `Son: ₺${sup.fiyat.son_fiyat.toLocaleString('tr-TR')}` : ''}
                                    </div>
                                </div>
                            </div>

                            {/* Uyarılar */}
                            {sup.uyarilar.length > 0 && (
                                <div style={{
                                    background: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    borderRadius: radius.md,
                                    padding: '15px',
                                    marginBottom: '20px'
                                }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>⚠️ Uyarılar</h4>
                                    {sup.uyarilar.map((u, i) => (
                                        <div key={i} style={{
                                            padding: '6px 0',
                                            fontSize: '13px',
                                            color: u.tip === 'kritik' ? '#dc3545' : '#856404',
                                            fontWeight: u.tip === 'kritik' ? 'bold' : 'normal',
                                            borderBottom: i < sup.uyarilar.length - 1 ? '1px solid #ffe69c' : 'none'
                                        }}>
                                            {u.tip === 'kritik' ? '🔴' : '⚠️'} {u.mesaj}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Skor Dağılımı */}
                            {sup.skor_parcalari && (
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '20px',
                                    marginBottom: '20px'
                                }}>
                                    <h4 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>📊 Skor Dağılımı</h4>
                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        {[
                                            { label: 'Kalite', key: 'kalite', max: 4, color: '#17a2b8' },
                                            { label: 'Randıman', key: 'randiman', max: 3, color: '#28a745' },
                                            { label: 'Fire', key: 'fire', max: 2, color: '#ffc107' },
                                            { label: 'Teslimat', key: 'teslimat', max: 1, color: '#6f42c1' }
                                        ].map(item => {
                                            const val = sup.skor_parcalari[item.key] || 0;
                                            const pct = (val / item.max) * 100;
                                            return (
                                                <div key={item.key} style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                                                        {item.label} ({val}/{item.max})
                                                    </div>
                                                    <div style={{
                                                        height: '8px',
                                                        background: '#f0f0f0',
                                                        borderRadius: radius.sm,
                                                        overflow: 'hidden'
                                                    }}>
                                                        <div style={{
                                                            width: `${pct}%`,
                                                            height: '100%',
                                                            background: item.color,
                                                            borderRadius: radius.sm,
                                                            transition: 'width 0.3s'
                                                        }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Aylık Trend Tablosu */}
                            {sup.aylik_trend.length > 0 && (
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '20px'
                                }}>
                                    <h4 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>📈 Aylık Trend</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: colors.backgroundAlt }}>
                                                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Ay</th>
                                                <th style={{ padding: '7px', textAlign: 'center', fontSize: '13px' }}>Kalite (1-5)</th>
                                                <th style={{ padding: '7px', textAlign: 'center', fontSize: '13px' }}>Randıman (%)</th>
                                                <th style={{ padding: '7px', textAlign: 'center', fontSize: '13px' }}>Fire (%)</th>
                                                <th style={{ padding: '7px', textAlign: 'center', fontSize: '13px' }}>Ort. Fiyat (₺)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sup.aylik_trend.map((t, i) => {
                                                const kaliteBg = t.kalite != null
                                                    ? (t.kalite >= 4 ? '#d4edda' : (t.kalite < 3 ? '#f8d7da' : '#fff3cd'))
                                                    : 'transparent';
                                                const randBg = t.randiman != null
                                                    ? (t.randiman >= 70 ? '#d4edda' : (t.randiman < 60 ? '#f8d7da' : '#fff3cd'))
                                                    : 'transparent';
                                                const fireBg = t.fire != null
                                                    ? (t.fire <= 3 ? '#d4edda' : (t.fire > 8 ? '#f8d7da' : '#fff3cd'))
                                                    : 'transparent';

                                                return (
                                                    <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                                        <td style={{ padding: '10px', fontWeight: 'bold', fontSize: '13px' }}>
                                                            {t.ay}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            textAlign: 'center',
                                                            background: kaliteBg,
                                                            fontWeight: 'bold',
                                                            fontSize: '13px'
                                                        }}>
                                                            {t.kalite ?? '—'}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            textAlign: 'center',
                                                            background: randBg,
                                                            fontWeight: 'bold',
                                                            fontSize: '13px'
                                                        }}>
                                                            {t.randiman != null ? `%${t.randiman}` : '—'}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            textAlign: 'center',
                                                            background: fireBg,
                                                            fontWeight: 'bold',
                                                            fontSize: '13px'
                                                        }}>
                                                            {t.fire != null ? `%${t.fire}` : '—'}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            textAlign: 'center',
                                                            fontSize: '13px'
                                                        }}>
                                                            {t.fiyat != null ? `₺${t.fiyat.toLocaleString('tr-TR')}` : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Teslimat Bilgisi */}
                            {sup.teslimat.toplam_teslimat > 0 && (
                                <div style={{
                                    background: colors.surface,
                                    borderRadius: radius.md,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    padding: '20px',
                                    marginTop: '20px'
                                }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>📦 Teslimat Özeti</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div>
                                            <div style={{ color: '#888', fontSize: '12px' }}>Toplam Teslimat</div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{sup.teslimat.toplam_teslimat}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#888', fontSize: '12px' }}>Miktar Tutarlılığı</div>
                                            <div style={{
                                                fontSize: '24px',
                                                fontWeight: 'bold',
                                                color: sup.teslimat.miktar_tutarliligi_pct >= 95 ? '#28a745' : '#ffc107'
                                            }}>
                                                %{sup.teslimat.miktar_tutarliligi_pct}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            background: colors.surface,
                            borderRadius: radius.md,
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                            padding: '40px',
                            textAlign: 'center',
                            color: colors.textTertiary
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                            <div>Detay görüntülemek için bir tedarikçi seçin</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TedarikçiKarnesiPage;
